/**
 * Configuration Service
 * Fetches frontend configuration from backend API instead of using environment variables
 * This centralizes all configuration and allows backend to control frontend settings
 */

// Cache for configuration to avoid repeated API calls
let configCache = null;
let configPromise = null;

/**
 * Get API URL for backend
 * This is the only hardcoded configuration - everything else comes from backend
 */
const getBackendUrl = () => {
  // Check if we're in development or production
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:5000';
  }
  // Production - use relative URL (empty string)
  // This ensures the request goes to the same domain as the frontend
  return '';
};

/**
 * Fetch configuration from backend
 * Uses caching to avoid repeated API calls
 */
export const fetchConfig = async () => {
  // Return cached config if available
  if (configCache) {
    return configCache;
  }

  // Return existing promise if fetch is already in progress
  if (configPromise) {
    return configPromise;
  }

  // Fetch configuration from backend
  configPromise = (async () => {
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/config/frontend`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch config: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch configuration');
      }

      // Cache the configuration
      configCache = data.config;
      
      console.log('✅ Frontend configuration loaded from backend:', {
        hasGoogleMapsKey: !!configCache.googleMapsApiKey,
        hasDataGovKey: !!configCache.dataGovApiKey,
        hasRazorpayKey: !!configCache.razorpayKeyId,
        hasGoogleClientId: !!configCache.googleClientId,
        hasFacebookAppId: !!configCache.facebookAppId,
        apiUrl: configCache.apiUrl
      });

      return configCache;
    } catch (error) {
      // Suppress verbose errors when server is not available
      if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError') || error.message?.includes('CONNECTION_REFUSED')) {
        console.warn('⚠️ Backend server is not available. Using fallback configuration.');
      } else {
        console.error('❌ Error fetching frontend config:', error);
      }
      
      // Return fallback configuration if backend is unavailable
      const fallbackConfig = {
        apiUrl: getBackendUrl(),
        googleMapsApiKey: '',
        dataGovApiKey: '',
        pincodeApiBase: 'https://api.data.gov.in/resource',
        pincodeResourceId: '6176ee09-3d56-4a3b-8115-0ac7bd90b84e',
        razorpayKeyId: 'rzp_test_R7tBMOOLxOu2dr',
        googleClientId: '',
        facebookAppId: '',
        frontendUrl: window.location.origin
      };
      
      configCache = fallbackConfig;
      return fallbackConfig;
    } finally {
      configPromise = null;
    }
  })();

  return configPromise;
};

/**
 * Get specific configuration value
 * @param {string} key - Configuration key to retrieve
 * @param {any} defaultValue - Default value if key not found
 */
export const getConfig = async (key, defaultValue = null) => {
  const config = await fetchConfig();
  return config[key] !== undefined ? config[key] : defaultValue;
};

/**
 * Clear configuration cache
 * Useful for testing or when configuration needs to be refreshed
 */
export const clearConfigCache = () => {
  configCache = null;
  configPromise = null;
};

/**
 * Get API URL (most commonly used config)
 */
export const getApiUrl = async () => {
  const config = await fetchConfig();
  return config.apiUrl || getBackendUrl();
};

/**
 * Get Google Maps API Key
 */
export const getGoogleMapsApiKey = async () => {
  const config = await fetchConfig();
  return config.googleMapsApiKey || '';
};

/**
 * Get Data.gov.in API Key
 */
export const getDataGovApiKey = async () => {
  const config = await fetchConfig();
  return config.dataGovApiKey || '';
};

/**
 * Get Pincode API configuration
 */
export const getPincodeApiConfig = async () => {
  const config = await fetchConfig();
  return {
    apiKey: config.dataGovApiKey || '',
    baseUrl: config.pincodeApiBase || 'https://api.data.gov.in/resource',
    resourceId: config.pincodeResourceId || '6176ee09-3d56-4a3b-8115-0ac7bd90b84e'
  };
};

/**
 * Get Razorpay Key ID
 */
export const getRazorpayKeyId = async () => {
  const config = await fetchConfig();
  return config.razorpayKeyId || 'rzp_test_R7tBMOOLxOu2dr';
};

/**
 * Get Google Client ID (for OAuth)
 */
export const getGoogleClientId = async () => {
  const config = await fetchConfig();
  return config.googleClientId || '';
};

/**
 * Get Facebook App ID (for OAuth)
 */
export const getFacebookAppId = async () => {
  const config = await fetchConfig();
  return config.facebookAppId || '';
};

// Export default object with all methods
const configService = {
  fetchConfig,
  getConfig,
  clearConfigCache,
  getApiUrl,
  getGoogleMapsApiKey,
  getDataGovApiKey,
  getPincodeApiConfig,
  getRazorpayKeyId,
  getGoogleClientId,
  getFacebookAppId
};

export default configService;




